import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatAPI } from '../api/axios';

export default function GuardrailsManagement() {
  const navigate = useNavigate();
  const [guardrailsStatus, setGuardrailsStatus] = useState({
    enabled: false,
    rate_limits: { enabled: false, max_requests_per_minute: 0 },
    blocked_words_count: 0,
    pii_detection_enabled: false,
    audit_log_count: 0
  });
  
  const [auditLogs, setAuditLogs] = useState([]);
  const [blockedWords, setBlockedWords] = useState([]);
  const [newBlockedWord, setNewBlockedWord] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [rateLimit, setRateLimit] = useState(60);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  const [filterBlocked, setFilterBlocked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('audit-logs');
  
  const [customInstructionInput, setCustomInstructionInput] = useState('');
  const [customInstructions, setCustomInstructions] = useState([]);
  
  const [commonInstructions, setCommonInstructions] = useState([]);
  const [agentInstructions, setAgentInstructions] = useState({});
  const [selectedAgent, setSelectedAgent] = useState('common');
  const [newInstruction, setNewInstruction] = useState('');
  
  const agents = [
    { id: 'common', name: 'Common (All Agents)' },
    { id: 'general_response_agent', name: 'General Response Agent' },
    { id: 'flight_booking_agent', name: 'Flight Booking Agent' },
    { id: 'image_analysis_agent', name: 'Image Analysis Agent' },
    { id: 'image_display_agent', name: 'Image Display Agent' }
  ];

  const showAlert = (message, type = 'info') => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 5000);
  };

  const addBlockedWord = async () => {
    if (!newBlockedWord.trim()) return;
    try {
      await chatAPI.updateGuardrailsConfig({
        config_type: 'blocked_words',
        config_data: { add_words: [newBlockedWord.trim()] }
      });
      showAlert(`Added "${newBlockedWord}" to blocked words`, 'success');
      setNewBlockedWord('');
      fetchGuardrailsStatus();
    } catch (error) {
      showAlert('Error connecting to server', 'error');
    }
  };
  
  const addInstruction = async () => {
    if (!newInstruction.trim()) return;
    const agentName = selectedAgent === 'common' ? null : selectedAgent;
    try {
      await chatAPI.updateGuardrailsConfig({
        config_type: 'instructions',
        config_data: {
          agent_name: agentName,
          add_instruction: newInstruction.trim()
        }
      });
      showAlert('Instruction added successfully', 'success');
      setNewInstruction('');
      fetchGuardrailsStatus();
      fetchAllInstructions();
      setActiveTab('instructions');
    } catch (error) {
      showAlert('Error connecting to server', 'error');
    }
  };
  
  const removeInstruction = async (instruction) => {
    const agentName = selectedAgent === 'common' ? null : selectedAgent;
    try {
      await chatAPI.updateGuardrailsConfig({
        config_type: 'instructions',
        config_data: {
          agent_name: agentName,
          remove_instruction: instruction
        }
      });
      showAlert('Instruction removed successfully', 'success');
      fetchAllInstructions();
    } catch (error) {
      showAlert('Error connecting to server', 'error');
    }
  };
  
  const removeBlockedWord = async (word) => {
    try {
      await chatAPI.updateGuardrailsConfig({
        config_type: 'blocked_words',
        config_data: { remove_words: [word] }
      });
      showAlert(`Removed "${word}" from blocked words`, 'success');
      fetchGuardrailsStatus();
    } catch (error) {
      showAlert('Error connecting to server', 'error');
    }
  };

  const fetchGuardrailsStatus = useCallback(async () => {
    try {
      const response = await chatAPI.getGuardrailsStatus();
      const data = response.data;
      setGuardrailsStatus(data);
      setRateLimit(data.rate_limits.max_requests_per_minute);
      
      // Fetch blocked words
      const configResponse = await chatAPI.updateGuardrailsConfig({ config_type: 'get_blocked_words' });
      const configData = configResponse.data;
      if (configData.blocked_words) {
        setBlockedWords(configData.blocked_words);
      }
    } catch (error) {
      console.error('Error fetching guardrails status:', error);
      showAlert('Error connecting to server', 'error');
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await chatAPI.getAuditLogs();
      const data = response.data;
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  }, []);

  const testGuardrails = async () => {
    if (!testQuery.trim()) return;
    
    try {
      const response = await chatAPI.testGuardrails({
        test_query: testQuery,
        custom_instructions: customInstructions
      });
      
      const data = response.data;
      setTestResult(data);
    } catch (error) {
      console.error('Error testing guardrails:', error);
      showAlert('Error connecting to server', 'error');
    }
  };

  const addCustomInstruction = () => {
    if (customInstructionInput.trim()) {
      setCustomInstructions([...customInstructions, customInstructionInput.trim()]);
      setCustomInstructionInput('');
    }
  };

  const removeCustomInstruction = (index) => {
    setCustomInstructions(customInstructions.filter((_, i) => i !== index));
  };



  const fetchAllInstructions = async () => {
    try {
      const response = await chatAPI.updateGuardrailsConfig({ config_type: 'get_all_instructions' });
      const data = response.data;
      if (data.success && data.instructions) {
        setCommonInstructions(data.instructions.common || []);
        setAgentInstructions(data.instructions.agents || {});
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
    }
  };

  useEffect(() => {
    fetchGuardrailsStatus();
    fetchAuditLogs();
    fetchAllInstructions();
    const interval = setInterval(() => {
      fetchGuardrailsStatus();
      fetchAuditLogs();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchGuardrailsStatus, fetchAuditLogs]);

  const filteredLogs = auditLogs.filter(log => {
    if (filterBlocked && !log.blocked) return false;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (log.query && log.query.toLowerCase().includes(term)) ||
        (log.response && log.response.toLowerCase().includes(term)) ||
        (log.user_id && log.user_id.toLowerCase().includes(term)) ||
        (log.agent && log.agent.toLowerCase().includes(term)) ||
        (log.reason && log.reason.toLowerCase().includes(term))
      );
    }
    
    return true;
  });

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f0f4f8',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        background: 'linear-gradient(90deg, #004a97 0%, #0066cc 100%)',
        padding: '20px 30px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            height: '40px',
            width: '120px',
            marginRight: '15px',
            backgroundColor: '#004a97',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '16px',
            borderRadius: '4px'
          }}>
            J9 AI Assistant
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Guardrails Management</h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8 }}>Monitor and configure AI safety guardrails</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/controlpanel')}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      <div style={{ 
        flex: 1, 
        padding: '30px',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%'
      }}>
        {alertMessage && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: '8px',
            backgroundColor: alertType === 'error' ? '#fee2e2' : alertType === 'success' ? '#d1fae5' : '#dbeafe',
            color: alertType === 'error' ? '#b91c1c' : alertType === 'success' ? '#047857' : '#1d4ed8',
            border: `1px solid ${alertType === 'error' ? '#fecaca' : alertType === 'success' ? '#a7f3d0' : '#93c5fd'}`,
          }}>
            {alertMessage}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '30px' }}>
          {/* Status Card */}
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Guardrails Status</h3>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span>Status:</span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: guardrailsStatus.enabled ? '#10b981' : '#ef4444',
                color: 'white',
                fontSize: '12px'
              }}>
                {guardrailsStatus.enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span>Rate Limiting:</span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: guardrailsStatus.rate_limits?.enabled ? '#10b981' : '#6b7280',
                color: 'white',
                fontSize: '12px'
              }}>
                {guardrailsStatus.rate_limits?.max_requests_per_minute || 0} req/min
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span>Blocked Words:</span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: '#3b82f6',
                color: 'white',
                fontSize: '12px'
              }}>
                {guardrailsStatus.blocked_words_count}
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Audit Logs:</span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: '#3b82f6',
                color: 'white',
                fontSize: '12px'
              }}>
                {guardrailsStatus.audit_log_count}
              </span>
            </div>
          </div>

          {/* Test Card */}
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Test Guardrails</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Test Instructions:
              </label>
              <div style={{ display: 'flex', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="Enter instruction"
                  value={customInstructionInput}
                  onChange={(e) => setCustomInstructionInput(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={addCustomInstruction}
                  style={{
                    marginLeft: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#004a97',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
              
              <div style={{
                maxHeight: '100px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: '#f9fafb'
              }}>
                {customInstructions.length > 0 ? (
                  customInstructions.map((instruction, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 8px',
                      marginBottom: '4px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      <span>{instruction}</span>
                      <button
                        onClick={() => removeCustomInstruction(index)}
                        style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#6b7280', fontSize: '12px', margin: 0 }}>No instructions added</p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Test Query:
              </label>
              <div style={{ display: 'flex' }}>
                <input
                  type="text"
                  placeholder="Enter test query"
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={testGuardrails}
                  style={{
                    marginLeft: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Test
                </button>
              </div>
            </div>

            {testResult && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#f9fafb'
              }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Test Results:</h4>
                
                <div style={{ marginBottom: '12px' }}>
                  <strong>Status:</strong>
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: testResult.allowed ? '#10b981' : '#ef4444',
                    color: 'white',
                    fontSize: '12px'
                  }}>
                    {testResult.allowed ? 'Allowed' : 'Blocked'}
                  </span>
                </div>
                
                {!testResult.allowed && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Reason:</strong> {testResult.reason}
                  </div>
                )}
                
                <div style={{ marginBottom: '12px' }}>
                  <strong>AI Response:</strong>
                  <div style={{
                    marginTop: '4px',
                    padding: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '12px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                      {testResult.response}
                    </pre>
                  </div>
                </div>
                
                {testResult.instructions && testResult.instructions.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Applied Instructions:</strong>
                    <ul style={{ margin: '4px 0 0 16px', fontSize: '11px' }}>
                      {testResult.instructions.map((inst, idx) => (
                        <li key={idx}>{inst}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {testResult.blocked_words && testResult.blocked_words.length > 0 && (
                  <div>
                    <strong>Test Blocked Words:</strong>
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {testResult.blocked_words.map((word, idx) => (
                        <span key={idx} style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px'
                        }}>
                          {word}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          marginBottom: '20px',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <button
            onClick={() => setActiveTab('audit-logs')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'audit-logs' ? '#004a97' : 'transparent',
              color: activeTab === 'audit-logs' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: activeTab === 'audit-logs' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'audit-logs' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            Audit Logs
          </button>
          <button
            onClick={() => setActiveTab('blocked-content')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'blocked-content' ? '#004a97' : 'transparent',
              color: activeTab === 'blocked-content' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: activeTab === 'blocked-content' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'blocked-content' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            Blocked Content
          </button>
          <button
            onClick={() => setActiveTab('instructions')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'instructions' ? '#004a97' : 'transparent',
              color: activeTab === 'instructions' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: activeTab === 'instructions' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'instructions' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            Agent Instructions
          </button>
        </div>

        {/* Audit Logs Tab */}
        {activeTab === 'audit-logs' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Audit Logs</h3>
            
            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filterBlocked}
                  onChange={(e) => setFilterBlocked(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Show only blocked
              </label>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Time</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Query</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor: log.blocked ? '#fef2f2' : index % 2 === 0 ? '#f8fafc' : 'white',
                          borderBottom: '1px solid #e5e7eb'
                        }}
                      >
                        <td style={{ padding: '12px', fontSize: '12px' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td style={{ padding: '12px', fontSize: '12px', maxWidth: '300px' }}>
                          {log.query?.substring(0, 100)}...
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: log.blocked ? '#ef4444' : '#10b981',
                            color: 'white',
                            fontSize: '10px'
                          }}>
                            {log.blocked ? 'Blocked' : 'Allowed'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', fontSize: '12px' }}>{log.reason || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ padding: '20px', textAlign: 'center' }}>No logs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Blocked Content Tab */}
        {activeTab === 'blocked-content' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Blocked Content Configuration</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
              <div>
                <h4 style={{ margin: '0 0 16px', fontSize: '16px' }}>Rate Limiting</h4>
                <div style={{ display: 'flex', marginBottom: '20px' }}>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={rateLimit}
                    onChange={(e) => setRateLimit(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px'
                    }}
                  />
                  <button
                    style={{
                      marginLeft: '8px',
                      padding: '8px 16px',
                      backgroundColor: '#004a97',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Update
                  </button>
                </div>

                <h4 style={{ margin: '0 0 16px', fontSize: '16px' }}>Add Blocked Word</h4>
                <div style={{ display: 'flex' }}>
                  <input
                    type="text"
                    placeholder="Enter word to block"
                    value={newBlockedWord}
                    onChange={(e) => setNewBlockedWord(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px'
                    }}
                  />
                  <button
                    onClick={addBlockedWord}
                    style={{
                      marginLeft: '8px',
                      padding: '8px 16px',
                      backgroundColor: '#004a97',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 16px', fontSize: '16px' }}>Blocked Words</h4>
                <div style={{
                  padding: '16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#f9fafb'
                }}>
                  {blockedWords.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {blockedWords.map((word, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {word}
                          <button
                            onClick={() => removeBlockedWord(word)}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280', margin: 0 }}>No blocked words configured</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions Tab */}
        {activeTab === 'instructions' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Agent Instructions</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Select Agent:</label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 16px', fontSize: '16px' }}>Add New Instruction</h4>
              <div style={{ display: 'flex', marginBottom: '10px' }}>
                <input
                  type="text"
                  placeholder="Enter instruction for the agent..."
                  value={newInstruction}
                  onChange={(e) => setNewInstruction(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px'
                  }}
                />
                <button
                  onClick={addInstruction}
                  style={{
                    marginLeft: '8px',
                    padding: '8px 16px',
                    backgroundColor: '#004a97',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Add
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    if (window.confirm('Clear all guardrails data?')) {
                      chatAPI.updateGuardrailsConfig({ config_type: 'clear_all_data' }).then(() => {
                        showAlert('All data cleared', 'success');
                        window.location.reload();
                      });
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🗑️ Clear All
                </button>
              </div>
            </div>
            
            <div>
              <h4 style={{ margin: '0 0 16px', fontSize: '16px' }}>Current Instructions for {selectedAgent === 'common' ? 'All Agents' : agents.find(a => a.id === selectedAgent)?.name}</h4>
              <div style={{
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#f9fafb',
                minHeight: '200px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {commonInstructions.length > 0 || (agentInstructions[selectedAgent] && agentInstructions[selectedAgent].length > 0) ? (
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    {(selectedAgent === 'common' ? commonInstructions : (agentInstructions[selectedAgent] || [])).map((instruction, index) => (
                      <li key={index} style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px' }}>{instruction}</span>
                        <button 
                          onClick={() => {
                            if (window.confirm('Delete this instruction?')) {
                              removeInstruction(instruction);
                            }
                          }}
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '3px 8px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: '#6b7280', textAlign: 'center', marginTop: '20px' }}>
                    No instructions configured for this agent
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: '#004a97',
        color: '#f8f9fa',
        padding: '20px',
        textAlign: 'center',
        fontSize: '14px',
        marginTop: '40px'
      }}>
        J9 AI Assistant Guardrails Management © 2025
      </div>
    </div>
  );
}